import type { ImageBatchListItem, VideoHistoryRow } from "../../api/types.js";

/**
 * The unified HISTORY view-model: every product output (a Video or an Image request) is ONE `HistoryItem` in ONE list,
 * sorted by ONE `historyTimestamp` - the time of its FINAL decision, newest first. Pure and DOM-free.
 */
export type HistoryItemType = "VIDEO" | "IMAGE";

export type HistoryItem =
  | { readonly type: "VIDEO"; readonly key: string; readonly historyTimestamp: string; readonly video: VideoHistoryRow }
  | { readonly type: "IMAGE"; readonly key: string; readonly historyTimestamp: string; readonly request: ImageBatchListItem };

/** The Video filter tabs (`HistoryTab` re-exports this as `HistoryFilter`). */
export type HistoryItemFilter = "ALL" | "ACTIVE" | "FINALIZED" | "FAILED" | "REJECTED" | "NEEDS_ATTENTION";

/** The server-computed final-decision time; an older API that does not send it falls back to the last update. */
export function videoHistoryTimestamp(video: VideoHistoryRow): string {
  return video.historyTimestamp ?? video.updatedAt;
}

/** An Image request is in HISTORY because it has a final decision: that decision time IS its history time. */
export function imageHistoryTimestamp(request: ImageBatchListItem): string {
  return request.finalDecidedAt ?? request.createdAt;
}

export function videoToHistoryItem(video: VideoHistoryRow): HistoryItem {
  return { type: "VIDEO", key: `video:${video.id}`, historyTimestamp: videoHistoryTimestamp(video), video };
}

export function imageToHistoryItem(request: ImageBatchListItem): HistoryItem {
  return { type: "IMAGE", key: `image:${request.id}`, historyTimestamp: imageHistoryTimestamp(request), request };
}

function timeOf(item: HistoryItem): number {
  const ms = Date.parse(item.historyTimestamp);
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

/** One list, newest final decision first. A row with an unparseable time sorts last; ties keep a stable, deterministic order (by key). */
export function sortHistoryItems(items: readonly HistoryItem[]): HistoryItem[] {
  return [...items].sort((a, b) => {
    const diff = timeOf(b) - timeOf(a);
    if (diff !== 0 && !Number.isNaN(diff)) return diff;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });
}

/** Merges the two independently-loaded sources; either may be empty (a failed load of one type never removes the other). */
export function mergeHistoryItems(videos: readonly VideoHistoryRow[], images: readonly ImageBatchListItem[]): HistoryItem[] {
  return sortHistoryItems([...videos.map(videoToHistoryItem), ...images.map(imageToHistoryItem)]);
}

/**
 * Filter tabs over the unified list. Video rows keep their existing per-tab rule (`matchesVideoFilter`, supplied by the caller so the
 * Video behavior stays exactly what it was). An Image request in HISTORY is always a FINAL outcome: Approved -> FINALIZED, Rejected ->
 * REJECTED, and it is never ACTIVE / FAILED / NEEDS ATTENTION.
 */
export function matchesHistoryFilter(item: HistoryItem, filter: HistoryItemFilter, matchesVideoFilter: (video: VideoHistoryRow, filter: HistoryItemFilter) => boolean): boolean {
  if (item.type === "VIDEO") {
    return matchesVideoFilter(item.video, filter);
  }
  switch (filter) {
    case "ALL":
      return true;
    case "FINALIZED":
      return item.request.finalDecision === "approved";
    case "REJECTED":
      return item.request.finalDecision === "rejected";
    default:
      return false;
  }
}
